import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Avatar, Chip, Button, CircularProgress,
  TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import { PersonAdd, PersonSearch, Search, Place, People } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AppNavigation from './AppNavigation';
import InviteButton from './InviteButton';
import SignInNudge from './SignInNudge';
import { useBuddies } from '../hooks/useBuddies';
import { authService } from '../services/authService';
import { useNotification } from './NotificationContext';
import { buddyService, UserPublicProfile } from '../services/buddyService';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

type SortOption = 'mutualBuddies' | 'diveCount' | 'name';

const SORT_LABELS: Record<SortOption, string> = {
  mutualBuddies: 'Most mutual buddies',
  diveCount: 'Most dives logged',
  name: 'Name (A–Z)',
};

/**
 * Standalone "find divers beyond who you already know" surface — browse every discoverable
 * diver, filter by home site, and sort by mutual buddies or activity, rather than the
 * name-only search buried in the "add buddy" flow.
 */
const DiscoverDivers: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;
  const { buddies, pending, sendRequest } = useBuddies(userId);

  const [divers, setDivers] = useState<UserPublicProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [homeSiteFilter, setHomeSiteFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('mutualBuddies');
  const [requestingId, setRequestingId] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    buddyService.discoverUsers()
      .then(results => { if (!cancelled) setDivers(results); })
      .catch(() => { if (!cancelled) showError('Failed to load divers'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const existingBuddyIds = useMemo(() => new Set([
    ...buddies.map(b => b.buddyUserId),
    ...pending.outgoing.map(b => b.buddyUserId),
  ]), [buddies, pending.outgoing]);

  const homeSites = useMemo(() => {
    const names = divers.map(d => d.homeSiteName).filter((n): n is string => !!n);
    return Array.from(new Set(names)).sort();
  }, [divers]);

  const visibleDivers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    let filtered = divers.filter(d => !existingBuddyIds.has(d.userId));
    if (normalizedQuery) {
      filtered = filtered.filter(d => d.displayName.toLowerCase().includes(normalizedQuery));
    }
    if (homeSiteFilter) {
      filtered = filtered.filter(d => d.homeSiteName === homeSiteFilter);
    }

    const sorted = [...filtered];
    switch (sortBy) {
      case 'mutualBuddies':
        sorted.sort((a, b) => (b.mutualBuddyCount ?? 0) - (a.mutualBuddyCount ?? 0));
        break;
      case 'diveCount':
        sorted.sort((a, b) => (b.diveCount ?? 0) - (a.diveCount ?? 0));
        break;
      case 'name':
        sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
        break;
    }
    return sorted;
  }, [divers, existingBuddyIds, query, homeSiteFilter, sortBy]);

  const handleAdd = useCallback(async (toUserId: string, displayName: string) => {
    setRequestingId(toUserId);
    try {
      await sendRequest(toUserId);
      showSuccess(`Buddy request sent to ${displayName}!`);
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to send buddy request');
    } finally {
      setRequestingId(null);
    }
  }, [sendRequest, showSuccess, showError]);

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="discover other divers"
          description="Browse divers beyond the people you already know — filter by home dive site and see how many buddies you have in common. Sign in or create a free account to get started."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 1000, mx: 'auto' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 3, mb: 0.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Discover Divers</Typography>
          <InviteButton />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Find divers beyond the people you already know
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Filter by name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            autoComplete="off"
          />
          <FormControl size="small" sx={{ minWidth: { sm: 200 } }}>
            <InputLabel id="discover-home-site-label">Home dive site</InputLabel>
            <Select
              labelId="discover-home-site-label"
              label="Home dive site"
              value={homeSiteFilter}
              onChange={(e) => setHomeSiteFilter(e.target.value)}
            >
              <MenuItem value="">Any site</MenuItem>
              {homeSites.map(site => (
                <MenuItem key={site} value={site}>{site}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { sm: 220 } }}>
            <InputLabel id="discover-sort-label">Sort by</InputLabel>
            <Select
              labelId="discover-sort-label"
              label="Sort by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
            >
              {Object.entries(SORT_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && visibleDivers.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <PersonSearch sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No divers match these filters
            </Typography>
          </Box>
        )}

        {!loading && visibleDivers.length > 0 && (
          <Stack spacing={1.5}>
            {visibleDivers.map(diver => (
              <Paper key={diver.userId} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <Avatar
                    src={diver.profilePhotoUrl ?? undefined}
                    sx={{ width: 44, height: 44, bgcolor: 'primary.main', fontWeight: 700, cursor: 'pointer' }}
                    onClick={() => navigate(`/u/${diver.userId}`)}
                  >
                    {!diver.profilePhotoUrl && (initials(diver.displayName) || '?')}
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => navigate(`/u/${diver.userId}`)}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{diver.displayName}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" color="text.secondary">{diver.diveCountLabel}</Typography>
                      {diver.homeSiteName && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Place sx={{ fontSize: 14 }} /> {diver.homeSiteName}
                        </Typography>
                      )}
                    </Stack>
                    {!!diver.mutualBuddyCount && (
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<People sx={{ fontSize: 14 }} />}
                        label={`${diver.mutualBuddyCount} mutual ${diver.mutualBuddyCount === 1 ? 'buddy' : 'buddies'}`}
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={requestingId === diver.userId ? <CircularProgress size={14} /> : <PersonAdd />}
                    disabled={requestingId === diver.userId}
                    onClick={() => handleAdd(diver.userId, diver.displayName)}
                    sx={{ minHeight: 36 }}
                  >
                    Add
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default DiscoverDivers;
